"use client"

import PropTypes from "prop-types"
import { Box, Grid, Typography, Paper, IconButton, Tooltip } from "@mui/material"
import moment from "moment"
import { ChatBubbleOutline, Info, Reply as ReplyIcon } from "@mui/icons-material"

const senderColorMap = {}

const getColorForSender = (senderName) => {
  if (!senderColorMap[senderName]) {
    senderColorMap[senderName] = getRandomColor()
  }
  return senderColorMap[senderName]
}

const getRandomColor = () => {
  const colors = [
    "#E91E63", "#9C27B0", "#673AB7", "#3F51B5", "#2196F3",
    "#00BCD4", "#009688", "#4CAF50", "#FF9800", "#FF5722",
    "#795548", "#607D8B",
  ]
  return colors[Math.floor(Math.random() * colors.length)]
}

const ChartHistory = ({ data, theme, user, ref, onReply }) => {
  return (
    <Grid
      container
      ref={ref}
      sx={{ minHeight: "75vh", py: 1, px: 1 }}
    >
      {data?.length === 0 ? (
        <Grid item xs={12}>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="100%"
            sx={{ textAlign: "center", opacity: 0.6, py: 8 }}
          >
            <ChatBubbleOutline sx={{ fontSize: 80, mb: 3, color: "text.secondary" }} />
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 300 }}>
              No messages yet
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Start the conversation and say 👋!
            </Typography>
          </Box>
        </Grid>
      ) : (
        data.map((history, index) => {
          const isCurrentUser = history.senderId === localStorage.getItem("userId")
          const isNotification = history?.notification

          if (isNotification) {
            return (
              <Grid item xs={12} key={index}>
                <Box display="flex" justifyContent="center" px={2} py={1}>
                  <Paper
                    elevation={0}
                    sx={{
                      maxWidth: "70%",
                      bgcolor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)",
                      color: theme.palette.text.secondary,
                      px: 2,
                      py: 1,
                      borderRadius: "8px",
                      textAlign: "center",
                      border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"}`,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                      <Info sx={{ fontSize: 12, opacity: 0.3 }} />
                      <Typography sx={{ fontSize: "0.75rem", fontWeight: 400, lineHeight: 1 }}>
                        {history?.content} {moment(history.timeStamp).format("MMM D • h:mm A")}
                      </Typography>
                    </Box>
                  </Paper>
                </Box>
              </Grid>
            )
          }

          return (
            <Grid item xs={12} key={index}>
              <Box display="flex" justifyContent={isCurrentUser ? "flex-end" : "flex-start"} px={2} py={0.5}>
                <Box sx={{
                  maxWidth: "75%", minWidth: "120px", position: "relative", "&:hover .reply-icon": {
                    display: "flex",
                  },
                }}>
                  <Paper
                    elevation={1}
                    sx={{
                      bgcolor: isCurrentUser ? "#DCF8C6" : theme.palette.mode === "dark" ? "#2A2F32" : "#FFFFFF",
                      color: isCurrentUser ? "#000000" : theme.palette.text.primary,
                      p: 1.5,
                      borderRadius: isCurrentUser ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                      boxShadow: theme.palette.mode === "dark"
                        ? "0 1px 2px rgba(0,0,0,0.3)"
                        : "0 1px 2px rgba(0,0,0,0.1)",
                      position: "relative",
                      "&::before": isCurrentUser
                        ? {
                          content: '""',
                          position: "absolute",
                          bottom: 0,
                          right: -8,
                          width: 0,
                          height: 0,
                          borderLeft: "8px solid #DCF8C6",
                          borderBottom: "8px solid transparent",
                        }
                        : {
                          content: '""',
                          position: "absolute",
                          bottom: 0,
                          left: -8,
                          width: 0,
                          height: 0,
                          borderRight: `8px solid ${theme.palette.mode === "dark" ? "#2A2F32" : "#FFFFFF"}`,
                          borderBottom: "8px solid transparent",
                        },
                    }}
                  >
                    {/* Show reply block if replying to someone */}
                    {history.repliedFromId && (
                      <Box
                        sx={{
                          backgroundColor: theme.palette.mode === "dark" ? "#1c1c1c" : "#f0f0f0",
                          borderLeft: "4px solid #1976d2",
                          px: 1,
                          py: 0.5,
                          borderRadius: "6px",
                          mb: 1,
                        }}
                      >
                        <Typography variant="caption" fontWeight={600} color="primary">
                          Replied To: {history?.receiver !== null ? history.receiver?.name : history?.repliedForName}
                        </Typography>
                        <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                          {history.repliedContent}
                        </Typography>
                      </Box>
                    )}

                    {!isCurrentUser && (
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: getColorForSender(history.senderName),
                          fontWeight: 600,
                          fontSize: "0.85rem",
                          mb: 0.5,
                          lineHeight: 1.2,
                        }}
                      >
                        {history?.senderName}
                      </Typography>
                    )}

                    <Typography
                      variant="body1"
                      sx={{
                        whiteSpace: "pre-wrap",
                        fontSize: "0.95rem",
                        lineHeight: 1.4,
                        wordBreak: "break-word",
                        mb: 1,
                      }}
                    >
                      {history?.content}
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{
                        color: isCurrentUser ? "rgba(0, 0, 0, 0.6)" : theme.palette.text.secondary,
                        fontSize: "0.7rem",
                        float: "right",
                        mt: 0.5,
                        lineHeight: 1,
                      }}
                    >
                      {moment(history.timeStamp).format("MMM D • h:mm A")}
                    </Typography>

                    <Box sx={{ clear: "both" }} />
                  </Paper>

                  {/* Reply button on hover */}
                  <Box
                    className="reply-icon"
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: isCurrentUser ? "auto" : -30,
                      left: isCurrentUser ? -30 : "auto",
                      display: "none", // Initially hidden
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Tooltip title="Reply">
                      <IconButton
                        size="small"
                        onClick={() => onReply?.(history)}
                      >
                        <ReplyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            </Grid>
          )
        })
      )}
    </Grid>
  )
}

ChartHistory.propTypes = {
  theme: PropTypes.object,
  data: PropTypes.array,
  user: PropTypes.object,
  ref: PropTypes.object,
  onReply: PropTypes.func, // ✅ reply handler
}

export default ChartHistory
